import {testIdProps} from '@github-ui/test-id-props'
import {ActionList, ActionMenu, type TokenProps} from '@primer/react'
import {use, useCallback, useEffect, useState} from 'react'

import type {LinkedPullRequest} from '../../api/common-contracts'
import {ItemType} from '../../api/memex-items/item-type'
import {FocusType} from '../../navigation/types'
import {tokenIconSize} from '../common/avatars-token'
import {ItemState} from '../item-state'
import {LinkedPullRequestGroup} from '../table/cells/linked-pull-requests-group'
import {moveTableFocus, useStableTableNavigation} from '../table/navigation'
import {FocusCellContext} from '../table/use-table-focus-cell'
import styles from './linked-pr-list.module.css'

export interface LinkedPullRequestListProps {
  linkedPullRequests: Array<LinkedPullRequest>
  isWrapping?: boolean
}

export const LinkedPullRequestList = ({linkedPullRequests, isWrapping}: LinkedPullRequestListProps) => {
  const [open, setOpen] = useState(false)
  const {navigationDispatch} = useStableTableNavigation()
  const {focusCellElement, wrapperRef, childRef} = use(FocusCellContext)

  const onOpenChange = useCallback(
    (newOpen: boolean) => {
      if (newOpen) {
        navigationDispatch(moveTableFocus({focusType: FocusType.Suspended}))
      } else {
        navigationDispatch(moveTableFocus({focusType: FocusType.Focus}))
        focusCellElement()
      }
      setOpen(newOpen)
    },
    [navigationDispatch, focusCellElement],
  )

  // do not allow button to be focused when the menu is closed
  useEffect(() => {
    if (!open && childRef?.current) {
      childRef.current.setAttribute('tabindex', '-1')
    } else if (childRef?.current) {
      childRef.current.setAttribute('tabindex', '0')
    }
  }, [open, childRef])

  return (
    <ActionMenu open={open} onOpenChange={onOpenChange} anchorRef={childRef}>
      <ActionMenu.Anchor tabIndex={-1}>
        <button className={styles.Trigger} {...testIdProps(`linked-prs-selector`)}>
          <LinkedPullRequestGroup linkedPullRequests={linkedPullRequests} isDecorative isWrapping={isWrapping} />
        </button>
      </ActionMenu.Anchor>
      <ActionMenu.Overlay onEscape={() => onOpenChange(false)} returnFocusRef={wrapperRef}>
        <ActionList>
          {linkedPullRequests.map(linkedPullRequest => {
            return (
              <ActionList.LinkItem
                href={linkedPullRequest.url}
                target="_blank"
                rel="noopener noreferrer"
                key={linkedPullRequest.id}
                {...testIdProps(`linked-prs-link-${linkedPullRequest.id}`)}
              >
                <LinkedPullRequestListItem
                  linkedPullRequest={linkedPullRequest}
                  tabIndex={-1}
                  key={linkedPullRequest.id}
                />
              </ActionList.LinkItem>
            )
          })}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

interface LinkedPullRequestReadOnlyTokenProps extends Omit<TokenProps, 'as' | 'leadingVisual' | 'text'> {
  linkedPullRequest: LinkedPullRequest
}

const LinkedPullRequestListItem = ({linkedPullRequest, ...tokenProps}: LinkedPullRequestReadOnlyTokenProps) => {
  return (
    <div>
      <ItemState
        isDraft={linkedPullRequest.isDraft}
        state={linkedPullRequest.state}
        type={ItemType.PullRequest}
        isBlocked={false}
        size={tokenIconSize(tokenProps.size)}
      />
      <span className={styles.Number}>#{linkedPullRequest.number}</span>
    </div>
  )
}
